import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useState } from 'react';

import { AuditComparison } from '@/components/audit/AuditComparison';
import { AuditNavigator } from '@/components/audit/AuditNavigator';
import { AdministrationBody } from '@/components/management/layout/AdministrationBody';
import { AdministrationHeader } from '@/components/management/layout/AdministrationHeader';
import type { Station } from '@/modules/api/backend';
import {
  backendApiStationsFindAllAuditsOptions,
  backendApiStationsFindOneAuditOptions,
} from '@/modules/api/backend/@tanstack/react-query.gen';

const i18n: Record<Exclude<keyof Station, '__caslSubjectType__'>, string> = {
  createdAt: 'Erstellt',
  deletedAt: 'Gelöscht',
  geometry: 'Standort',
  id: 'ID',
  name: 'Name',
  numBikeParkingCovered: 'Fahrradstellplätze überdacht',
  numBikeParkingStation: 'Fahrradabstellplätze stationsweit',
  numBikeReadyToRide: 'Fahrbereite Fahrräder',
  numBrFacility: 'Anzahl B+R Anlagen',
  rmvId: 'Bahnhofsnummer',
  rmvNumber: 'RMV-Nummber',
  stationComment: 'Hinweis',
  type: 'Stationstyp',
  updatedAt: 'Geändert',
  parkAndRideFacilities: 'Park + Ride Anlagen',
};

export const Route = createFileRoute('/_authenticated/management/stations/$stationId/audits')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: StationAuditsRoute,
  beforeLoad: () => ({ breadcrumb: 'Bearbeitungsverlauf' }),
});

function StationAuditsRoute() {
  const { stationId } = Route.useParams();

  const { data: audits } = useSuspenseQuery(
    backendApiStationsFindAllAuditsOptions({
      path: {
        stationId,
      },
    }),
  );

  const [index, setIndex] = useState(audits.length - 1);

  const { data: oldAudit, isLoading: isLoadingOld } = useQuery({
    ...backendApiStationsFindOneAuditOptions({
      path: {
        stationId,
        auditCreatedAt: audits[index - 1]?.createdAt as string,
      },
    }),
    enabled: !!audits[index - 1],
  });
  const { data: newAudit, isLoading: isLoadingNew } = useQuery({
    ...backendApiStationsFindOneAuditOptions({
      path: {
        stationId,
        auditCreatedAt: audits[index]?.createdAt as string,
      },
    }),
    enabled: !!audits[index],
  });

  return (
    <Fragment>
      <AdministrationHeader
        label="Station Bearbeitungsverlauf"
        links={[{ name: 'Bearbeitungsverlauf', options: { to: '/management/stations/$stationId' }, icon: 'CLOCK' }]}
      />
      <AdministrationBody>
        {!isLoadingOld && !isLoadingNew && (
          <Fragment>
            <AuditNavigator entries={audits} index={index} onChange={setIndex} />
            <AuditComparison i18n={i18n} newAudit={newAudit} oldAudit={oldAudit} />
          </Fragment>
        )}
      </AdministrationBody>
    </Fragment>
  );
}
